import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import NotificationToast from './components/NotificationToast';
import JobProgressIndicator from './components/JobProgressIndicator';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import WardrobePage from './pages/WardrobePage';
import OutfitsPage from './pages/OutfitsPage';
import OutfitDetailPage from './pages/OutfitDetailPage';
import TryOnPage from './pages/TryOnPage';
import HistoryPage from './pages/HistoryPage';
import ProfilePage from './pages/ProfilePage';
import './App.css';

function BottomNav() {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) return null;

  const navItems = [
    { path: '/wardrobe', icon: '/assets/icons/wardrobe-icon.png', label: 'Wardrobe' },
    { path: '/outfits', icon: '/assets/icons/outfit-icon.png', label: 'Outfits' },
    { path: '/try-on', icon: '/assets/icons/tryon-icon.png', label: 'Try On' },
    { path: '/profile', icon: '/assets/icons/profile-icon.png', label: 'Profile' },
  ];

  return (
    <nav className="bottom-nav">
      {navItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={`bottom-nav-item ${location.pathname === item.path ? 'active' : ''}`}
        >
          <span className="bottom-nav-icon">
            <img src={item.icon} alt={item.label} />
          </span>
          <span className="bottom-nav-label">{item.label}</span>
        </Link>
      ))}
    </nav>
  );
}

function TopBar() {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) return null;

  const pageTitles = {
    '/wardrobe': 'My Looksy',
    '/outfits': 'My Outfits',
    '/try-on': 'Virtual Try-On',
    '/history': 'History',
    '/profile': 'Profile',
  };

  const isWardrobePage = location.pathname === '/wardrobe';

  return (
    <div className="top-bar">
      {isWardrobePage ? (
        <img 
          src="/assets/branding/logo-text.png" 
          alt="Looksy" 
          className="top-bar-logo"
        />
      ) : (
        <h1 className="top-bar-title">{pageTitles[location.pathname] || 'Looksy'}</h1>
      )}
    </div>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
}

function AppContent() {
  const { user } = useAuth();
  
  return (
    <WebSocketProvider userId={user?.id}>
      <div className="App">
        <NotificationToast />
        <JobProgressIndicator />
        <TopBar />
        <div className="app-content">
          <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route
                path="/wardrobe"
                element={
                  <PrivateRoute>
                    <WardrobePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/outfits"
                element={
                  <PrivateRoute>
                    <OutfitsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/outfits/:id"
                element={
                  <PrivateRoute>
                    <OutfitDetailPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/try-on"
                element={
                  <PrivateRoute>
                    <TryOnPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/history"
                element={
                  <PrivateRoute>
                    <HistoryPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <ProfilePage />
                  </PrivateRoute>
                }
              />
              <Route path="/" element={<Navigate to="/wardrobe" replace />} />
            </Routes>
          </div>
          <BottomNav />
        </div>
      </WebSocketProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
